<template>
  <div>
    <div
      v-if="title"
      class="pb-1 text-xs"
    >
      {{ title }}
    </div>
    <div class="relative">
      <div class="flex items-start gap-1">
        <SfInput
          v-bind="$attrs"
          :id="id"
          :model-value="modelValue"
          :name="name"
          :for="name"
          :label="label"
          :valid="valid"
          :required="required"
          :type="type"
          :has-show-password="hasShowPassword"
          :disabled="disabled"
          class="primary-input flex-1"
          :class="{
            'primary-input--with-inside-top-label': !title && !!label,
            'primary-input--without-inside-top-label': !!title,
            'primary-input--with-required-star': required,
            'primary-input--with-prefix': prefix,
          }"
          @update:model-value="onInput"
          @blur="$emit('blur')"
        >
          <template
            v-if="loading"
            #show-password
          >
            <SpinnerLoader
              class="primary-input__loading sf-input__password-button"
              size="30px"
            />
          </template>
        </SfInput>
        <span
          v-if="required"
          class="required-star w-5 h-11 flex items-center justify-center absolute top-0
          cursor-default text-EXPONDO-red"
          :class="{ 'left-10': prefix }"
        >
          *
        </span>
      </div>
      <span
        v-if="prefix"
        data-testid="primary-input__prefix"
        class="w-10 h-[45px] border rounded-l flex items-center justify-center absolute top-0
          cursor-default bg-EXPONDO-grey-5 text-EXPONDO-grey-60"
      >
        {{ prefix }}
      </span>
      <InputErrorMessage
        :show="showError"
        :message="errorMessage"
        :has-messages="hasAnyMessage"
        test-id="primary-input__error-message"
      >
        <template #error>
          <slot name="error-message">
            {{ errorMessage }}
          </slot>
        </template>
        <transition name="sf-fade">
          <slot name="success-message">
            <div
              v-if="showSuccess"
              data-testid="primary-input__success-message"
              class="text-EXPONDO-green"
            >
              {{ successMessage }}
            </div>
          </slot>
        </transition>
        <transition name="sf-fade">
          <slot name="warning-message">
            <div
              v-if="showWarning"
              class="text-EXPONDO-gold"
            >
              {{ $t(warningMessage) }}
            </div>
          </slot>
        </transition>
        <transition name="sf-fade">
          <slot name="usage-tip">
            <div
              v-if="showUsageTip"
              class="font-normal"
            >
              {{ $t(usageTip) }}
            </div>
          </slot>
        </transition>
      </InputErrorMessage>
    </div>
  </div>
</template>

<script lang="ts">

export default defineComponent({
  name: 'PrimaryInput',

  props: {
    id: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    title: {
      type: String,
      default: '',
    },
    hasShowPassword: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    successMessage: {
      type: String,
      default: '',
    },
    warningMessage: {
      type: String,
      default: '',
    },
    usageTip: {
      type: String,
      default: '',
    },
    valid: {
      type: Boolean,
      default: true,
    },
    prefix: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
  },
  emits: ['blur', 'update:modelValue'],
  setup(props, { emit }) {

    const onInput = (newValue: string | number) => {

      emit('update:modelValue', newValue);

    };

    const showError = computed(() => !props.valid && !!props.errorMessage);
    const showSuccess = computed(() => !!props.successMessage && props.valid && !!props.modelValue);
    const showWarning = computed(() => !!props.warningMessage);
    const showUsageTip = computed(() => !!props.usageTip);
    const hasAnyMessage = computed(() => (
      showError.value || showSuccess.value || showWarning.value || showUsageTip.value
    ));

    return {
      onInput,
      showError,
      showSuccess,
      showWarning,
      showUsageTip,
      hasAnyMessage,
    };
  
  },
});

</script>

<style lang="scss">
.primary-input {
  /* Every rule below reads the horizontal insets from these two variables, so the
     text stays put across the focused / blurred / has-text states. Modifiers
     override the variable rather than re-declaring `padding`. */
  --primary-input-padding-x: 1rem;
  --primary-input-padding-left: var(--primary-input-padding-x);

  .sf-input__wrapper input:focus ~ .sf-input__label {
    display: none !important;
  }

  button[aria-label="switch-visibility-password"] {
    width: unset;
    right: 1rem;
  }

  .primary-input__loading {
    position: absolute;
  }

  .sf-input__wrapper {
    --input-height: 45px;
    --input-margin: 0;

    height: var(--input-height);
  }

  .sf-input__wrapper input {
    @apply border;

    border-radius: var(--form-border-radius);
    padding:
      0.7rem var(--primary-input-padding-x) 0.7rem
      var(--primary-input-padding-left);
    background-color: var(--c-white);
    background-repeat: no-repeat;
    background-position: right;
    height: var(--input-height);
  }

  .sf-input__wrapper input:disabled,
  .sf-input__wrapper input:read-only {
    @apply bg-EXPONDO-grey-5;

    -webkit-text-fill-color: var(--c-text);
  }

  .sf-input__bar {
    display: none;
  }

  .sf-input__label {
    @apply text-EXPONDO-grey-60;

    margin-left: var(--primary-input-padding-left);
    font-family: var(--font-family--primary);
    font-size: var(--font-size--sm);
    line-height: var(--h4-line-height);
    pointer-events: none;
  }

  .sf-input__label::after {
    display: none;
  }

  &.has-text {
    &.primary-input--with-inside-top-label {
      .sf-input__wrapper input {
        padding:
          1.1rem var(--primary-input-padding-x) 0.3rem
          var(--primary-input-padding-left);
      }
    }

    &.primary-input--without-inside-top-label {
      .sf-input__label {
        @apply sr-only;
      }
    }

    .sf-input__wrapper .sf-input__label {
      font-size: var(--font-size--xs);
    }

    .sf-input__wrapper input:focus {
      padding:
        0.7rem var(--primary-input-padding-x) 0.7rem
        var(--primary-input-padding-left);
    }
  }

  .sf-input__error-message {
    display: none;
  }

  /* Reserve room for the `.required-star` overlay. */
  &.primary-input--with-required-star {
    --primary-input-padding-left: 20px;
  }

  /* Declared after the star so the badge wins when a field has only a prefix. */
  &.primary-input--with-prefix {
    --primary-input-padding-left: 3rem;

    /* With both, the star is shifted past the badge (`left-10`), so the text
       has to clear the badge and the star. */
    &.primary-input--with-required-star {
      --primary-input-padding-left: calc(2.5rem + 20px);
    }
  }
}
</style>